import React, { useCallback, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Paper, Tooltip, Typography, Box } from '@mui/material'
import { GridCell } from '@mui/x-data-grid'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined'
import AssignmentLate from '@mui/icons-material/AssignmentLate'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StarIcon from '@mui/icons-material/Star'

import { APPS_TABLE_COLUMNS, GUILD_TABLE_COLUMNS, PRESCRIPTOR_TABLE_COLUMNS } from '../../constants'
import { TableUrlPagination } from '../../../../ui'
import { appsTableStyles } from './apps-table.styles'
import { setSelectedApps } from '../../slices'
import { applicationDataPreparation } from '../../logics'

const getLookup = (combo) => {
  const lookup = {}
  combo.forEach(({ key, value }) => {
    lookup[key] = value
  })
  return lookup
}

export const AppsTable = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const {
    data,
    search,
    pagination: { total_solicitudes: totalEntries },
    isLoading: loading,
    selectedRows,
  } = useSelector((state) => state.applications)

  useEffect(() => {
    dispatch(setSelectedApps([]))
  }, []) // eslint-disable-line

  const handleSelectRows = useCallback(
    (rows) => {
      dispatch(setSelectedApps(rows))
    },
    [dispatch]
  )

  const combos = useSelector((state) => state.combos)
  const { role } = useSelector((state) => state.global)
  const columns =
    role === 'gremio'
      ? GUILD_TABLE_COLUMNS
      : role === 'administrador_prescriptor'
        ? PRESCRIPTOR_TABLE_COLUMNS
        : APPS_TABLE_COLUMNS

  const formattedColumns = useMemo(() => {
    return columns.map((column) => {
      // Base column configuration for Material React Table
      let baseColumn = {
        accessorKey: column.field,
        id: column.field,
        size: column.width,
        minSize: column.minWidth || column.width,
        maxSize: column.maxWidth,
        enableSorting: column.sorting !== false,
        muiTableHeadCellProps: {
          align: column.align || 'center',
        },
        muiTableBodyCellProps: {
          align: column.align || 'center',
          sx: {
            minWidth: column.minWidth || column.width,
          },
        },
      }

      // Handle nested field accessors
    if (column.field === 'instalador') {
      baseColumn.accessorFn = (row) =>
        row.instalador_prescriptor?.razon_social ||
        (row.tipo_solicitud === 'prescriptor' ? row.empresa_ca?.nombre : null) ||
        row.instalador?.empresa?.razon_social ||
        row.instalador?.empresa?.nombre ||
        '--';
    }

      if (column.field === 'origen_instalador') {
        baseColumn.accessorFn = (row) => {
          if (row.instalador_prescriptor?.razon_social) return 'Propio'
          if (row.instalador_prescriptor == null)
            return 'Marketgas'
          return '--'
        }
      }

      // Handle special icon columns
      if (column.field === 'comunicaciones_pendientes') {
        return {
          ...baseColumn,
          header: 'Comunicaciones',
          muiTableHeadCellProps: {
            ...baseColumn.muiTableHeadCellProps,
            sx: {
              lineHeight: 0,
              '&&': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '&.MuiTableCell-head': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiTableSortLabel-root': {
                width: 0,
                padding: 0,
                margin: 0,
                visibility: 'hidden',
              },
              '.MuiTable-root &.MuiTableCell-head': {
                paddingLeft: 0,
              },
              '& > div, & div[class*="css-"]': {
                paddingLeft: 0,
              },
            },
          },
          Header: () => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip
                placement="top"
                title={
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    Comunicaciones
                  </Typography>
                }
              >
                <NotificationsNoneIcon sx={{ width: '1em', height: '1em' }} />
              </Tooltip>
            </Box>
          ),
          Cell: ({ row }) => {
            if (row.original.comunicaciones_pendientes > 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NotificationsActiveIcon color="secondary" sx={{ fontSize: 22 }} />
                </Box>
              )
            }
            return null
          },
        }
      }

      if (column.field === 'pendiente_peticionario') {
        return {
          ...baseColumn,
          header: 'Tareas',
          muiTableHeadCellProps: {
            ...baseColumn.muiTableHeadCellProps,
            sx: {
              lineHeight: 0,
              '&&': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '&.MuiTableCell-head': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiTableSortLabel-root': {
                width: 0,
                padding: 0,
                margin: 0,
                visibility: 'hidden',
              },
              '.MuiTable-root &.MuiTableCell-head': {
                paddingLeft: 0,
              },
              '& > div, & div[class*="css-"]': {
                paddingLeft: 0,
              },
            },
          },
          Header: () => (
            <Tooltip
              placement="top"
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  Tareas
                </Typography>
              }
            >
              <AssignmentOutlined sx={{ width: '1em', height: '1em' }} />
            </Tooltip>
          ),
          Cell: ({ row }) => {
            if (row.original.pendiente_peticionario > 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AssignmentLate color="secondary" sx={{ fontSize: 22 }} />
                </Box>
              )
            }
            return null
          },
        }
      }

      if (column.field === 'valoraciones_pendientes') {
        return {
          ...baseColumn,
          header: 'Valoraciones',
          muiTableHeadCellProps: {
            ...baseColumn.muiTableHeadCellProps,
            sx: {
              lineHeight: 0,
              '&&': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '&.MuiTableCell-head': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiTableSortLabel-root': {
                width: 0,
                padding: 0,
                margin: 0,
                visibility: 'hidden',
              },
              '.MuiTable-root &.MuiTableCell-head': {
                paddingLeft: 0,
              },
              '& > div, & div[class*="css-"]': {
                paddingLeft: 0,
              },
            },
          },
          Header: () => (
            <Tooltip
              placement="top"
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  Valoraciones
                </Typography>
              }
            >
              <StarBorderIcon sx={{ width: '1em', height: '1em' }} />
            </Tooltip>
          ),
          Cell: ({ row }) => {
            if (row.original.valoraciones_pendientes > 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StarIcon color="secondary" sx={{ fontSize: 22 }} />
                </Box>
              )
            }
            return null
          },
        }
      }

      // Handle combo columns
      if (column.comboId && combos[column.comboId]?.data) {
        const lookup = getLookup(combos[column.comboId].data)
        return {
          ...baseColumn,
          header: intl.formatMessage({
            id: `pages.applications.table.columns.${column.field}.title`,
          }),
          Cell: ({ cell }) => {
            const value = cell.getValue()
            const label = lookup[value] ?? value
            return <span>{label}</span>
          },
        }
      }

      // Handle regular columns
      return {
        ...baseColumn,
        header: intl.formatMessage({
          id: `pages.applications.table.columns.${column.field}.title`,
        }),
        Cell: ({ cell }) => {
          const v = cell.getValue()
          const isEmpty = v === null || v === undefined || v === ''

          if (isEmpty) {
            return <span>--</span>
          }

          if (v instanceof Date) {
            return <span>{v.toLocaleDateString('es-ES')}</span>
          }

          if (typeof v !== 'string') {
            return <span>{String(v)}</span>
          }

          return <span>{v}</span>
        },
      }
    })
  }, [columns, combos, intl])

  const processedData = useMemo(() => {
    const selectedIds = new Set(selectedRows.map((r) => r.id))
    return applicationDataPreparation(columns, data, { intl }).map((row) => {
      return row
    })
  }, [columns, data, intl, selectedRows])

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: true,
      tableLayout: 'fixed',
      draggable: false,
      headerHeight: 64,
    }),
    []
  )

  const handleRowClick = useCallback(
    (_, row) => {
      navigate(`/applications/${row.id}${location.search}`, {
        state: { prevSearch: location.search },
      })
    },
    [navigate, location.search]
  )
  console.log('')

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
    />
  )
}
